import React, { useMemo, useState } from 'react';
import {
  List,
  Datagrid,
  TextInput,
  Pagination,
  TextField,
  ReferenceField,
  useListContext
} from 'react-admin';

import get from 'lodash/get';
import { makeStyles } from '@material-ui/core';
import ListTitleBar from './fundtagimport/ExcelFundTagImportTitleBarWithDropbox';
import LinkField from '../../core/components/LinkField';
import Filter from '../../core/components/list/StyledFilter';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import PopupField from '../../core/components/grid/PopupField';
import Empty from '../../core/components/list/Empty';
import { FundTagIcon } from '../../core/themes/icons/FundTagIcon';

const useStyles = makeStyles({
  nameColumnStyle: {
    width: '45%'
  },
  inputCustomWidth: {
    width: '25%'
  },
  formFullWidth: {
    width: '100%',
    marginTop: '0px',
    marginBottom: '-8px',
    '& > div': {
      width: '100%'
    }
  },
  tag: {
    color: '#212121',
    'font-size': '12px',
    'line-height': '12px',
    textDecoration: 'none',
    'box-sizing': 'border-box',

    background: '#F5F5F5',
    border: '1px solid #E0E0E0',
    'border-radius': '2px',
    padding: '0 5px'
  },
  funds: {
    color: '#2962FF',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  button: {
    justifyContent: 'flex-start'
  }
});

const TagsTitle = props => {
  const { selectedIds } = useListContext();
  return (
    <ListTitleBar
      type="Fund Tags"
      title="Create New Fund Tag"
      createUrl="#/fundtags/create"
      hasCreatePermission={props.hasCreatePermission}
      hasImportFundTagExcelPermission={props.hasImportFundTagExcelPermission}
      hasExportFundTagExcelPermission={props.hasExportFundTagExcelPermission}
      fundTagNameFilter={props.fundTagNameFilter}
      icon={<FundTagIcon />}
      selectedIds={selectedIds}
    />
  );
};

const FundTagNameFilters = ({ ...props }) => {
  const classes = useStyles();

  const handleNameFilterChange = event => {
    props.setFundTagNameFilter(event.target.value);
  };

  return (
    <Filter {...props} variant="standard" className={classes.formFullWidth}>
      <TextInput
        label="Search by Fund Tag Name"
        onChange={handleNameFilterChange}
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
        className={classes.inputCustomWidth}
      />
    </Filter>
  );
};

const FundField = ({ record, ...rest }) => {
  const classes = useStyles();
  const fundIds = get(record, 'fundIds', []);

  if (fundIds.length > 1) {
    const getFundsList = () => {
      return fundIds.sort().map((_fundId, index) => (
        <ReferenceField
          source={'fundIds[' + index + ']'}
          reference="funds"
          link={false}
          {...rest}
        >
          <TextField source="name" />
        </ReferenceField>
      ));
    };
    return (
      <div>
        <PopupField
          data={getFundsList()}
          classes={{ button: classes.button }}
          label={`${record.fundIds.length} funds`}
          headLabel="Funds"
        />
      </div>
    );
  } else if (fundIds.length === 1) {
    return (
      <ReferenceField
        source="fundIds[0]"
        reference="funds"
        link={false}
        {...rest}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }

  return <span>0 Funds</span>;
};

const FundTagList = props => {
  const classes = useStyles();
  const { hasDeletePermission, hasCreatePermission, ...rest } = props;

  const { hasImportFundTagExcelPermission } = useMemo(() => {
    return {
      hasImportFundTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_FUND_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportFundTagExcelPermission } = useMemo(() => {
    return {
      hasExportFundTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_FUND_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const [fundTagNameFilter, setFundTagNameFilter] = useState('');

  return (
    <List
      {...rest}
      hasCreate={false}
      title={
        <TagsTitle
          hasCreatePermission={hasCreatePermission}
          hasImportFundTagExcelPermission={hasImportFundTagExcelPermission}
          hasExportFundTagExcelPermission={hasExportFundTagExcelPermission}
          fundTagNameFilter={fundTagNameFilter}
        />
      }
      sort={{ field: 'name', order: 'ASC' }}
      exporter={false}
      perPage={20}
      filters={
        <FundTagNameFilters setFundTagNameFilter={setFundTagNameFilter} />
      }
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={
        hasDeletePermission && (
          <BulkActionDeleteButton confirmContent="ra.message.confirm_delete_tags" />
        )
      }
      empty={<Empty />}
    >
      <Datagrid classes={{ thead: hasDeletePermission }}>
        <LinkField
          label="Fund Tag Name"
          source="name"
          headerClassName={classes.nameColumnStyle}
          className={classes.tag}
        />
        <TextField label="Description" source="description" />
        <FundField label="Assigned To:" />
      </Datagrid>
    </List>
  );
};
export default FundTagList;
